import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/api/client";
import { PageHeader, EmptyState } from "@/components/common";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Loader2, Search, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { confirmDialog } from "@/components/ui/confirm";

export default function MasterCrudPage({
  endpoint, title, breadcrumbs = [], columns, formFields = [],
  testIdPrefix = "master", initialForm = {},
  canCreate = true, canEdit = true, canDelete = true,
  beforeSubmit, transformOnLoad,
  customActions,
  queryParams = {},   // server-side params merged into API call (triggers reload on change)
  filterBar,         // custom filter JSX rendered above table; hides built-in search when provided
}) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  // client-side search + sort
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const qpKey = JSON.stringify(queryParams);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint, { params: { page, limit: 50, ...queryParams } });
      let data = r.data?.data || [];
      if (transformOnLoad) data = data.map(transformOnLoad);
      setRows(data);
      setMeta(r.data?.meta || {});
    } finally {
      setLoading(false);
    }
  }, [page, qpKey]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // reset page when server-side filters change
  useEffect(() => { setPage(1); setSearch(""); setSortKey(null); }, [qpKey]); // eslint-disable-line

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const displayed = useMemo(() => {
    let data = rows;
    if (!filterBar && search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(r =>
        columns.some(c => !c.render && String(r[c.key] ?? "").toLowerCase().includes(q))
      );
    }
    if (sortKey) {
      data = [...data].sort((a, b) => {
        const va = a[sortKey] ?? "";
        const vb = b[sortKey] ?? "";
        const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return data;
  }, [rows, search, sortKey, sortDir, filterBar, columns]);

  const openCreate = () => { setEditing(null); setForm(initialForm); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...initialForm, ...row }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let payload = { ...form };
      if (beforeSubmit) payload = beforeSubmit(payload);
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (row) => {
    const label = row.name || row.title || row.code || row.template_name || row.module_name;
    if (!(await confirmDialog({ title: "Delete", message: `Delete "${label}"? This cannot be undone.`, confirmText: "Delete", danger: true }))) return;
    try {
      await api.delete(`${endpoint}/${row.id}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(err?.response?.data?.message || "Failed"); }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ChevronsUpDown size={11} className="opacity-0 group-hover:opacity-50 transition-opacity" />;
    return sortDir === "asc" ? <ChevronUp size={11} className="text-[#334E7B]" /> : <ChevronDown size={11} className="text-[#334E7B]" />;
  };

  return (
    <div data-testid={`${testIdPrefix}-page`}>
      <PageHeader
        title={title}
        breadcrumbs={breadcrumbs}
        actions={canCreate && (
          <button onClick={openCreate} className="erp-btn-primary inline-flex items-center gap-2" data-testid={`${testIdPrefix}-new-btn`}>
            <Plus size={14} /> New
          </button>
        )}
      />

      {/* Filter bar — custom if provided, otherwise built-in search */}
      {filterBar ? (
        <div className="mb-4">{filterBar}</div>
      ) : (
        <div className="erp-card p-3 mb-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${title.toLowerCase()}...`}
              className="erp-input pl-8 text-sm"
              data-testid={`${testIdPrefix}-search`}
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                <X size={12} />
              </button>
            )}
          </div>
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {search ? `${displayed.length} of ${rows.length}` : `${rows.length} record${rows.length !== 1 ? "s" : ""}`}
          </span>
        </div>
      )}

      <div className="card overflow-x-auto">
        <div className="card-head">
          <h3><span className="dot" />{title}</h3>
          <span className="text-xs text-slate-500 whitespace-nowrap">{rows.length} record{rows.length !== 1 ? "s" : ""}</span>
        </div>
        {loading ? (
          <div className="p-12 flex items-center justify-center text-slate-500"><Loader2 className="animate-spin mr-2" /> Loading...</div>
        ) : displayed.length === 0 ? (
          <EmptyState title={search ? "No matches" : "No records"} hint={search ? `No ${title.toLowerCase()} match "${search}".` : "Nothing to show yet."} />
        ) : (
          <table className="erp-table">
            <thead>
              <tr>
                {columns.map(c => (
                  <th key={c.key} style={{ width: c.width }}
                    onClick={() => toggleSort(c.key)}
                    className="cursor-pointer select-none group">
                    <div className="flex items-center gap-1.5">
                      {c.label}
                      <SortIcon k={c.key} />
                    </div>
                  </th>
                ))}
                {(canEdit || canDelete || customActions) && <th></th>}
              </tr>
            </thead>
            <tbody>
              {displayed.map((r, i) => (
                <tr key={r.id ?? r.key ?? i} data-testid={`${testIdPrefix}-row-${r.id}`}>
                  {columns.map(c => (
                    <td key={c.key}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>
                  ))}
                  {(canEdit || canDelete || customActions) && (
                    <td>
                      <div className="flex items-center gap-1 justify-end">
                        {customActions && customActions(r, load)}
                        {canEdit && (
                          <button onClick={() => openEdit(r)} className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-sm" data-testid={`${testIdPrefix}-edit-${r.id}`}><Edit size={14} /></button>
                        )}
                        {canDelete && (
                          <button onClick={() => remove(r)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm" data-testid={`${testIdPrefix}-delete-${r.id}`}><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination (for server-side large datasets) */}
      {meta?.total_pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="erp-btn-secondary disabled:opacity-50">Prev</button>
          <span className="text-slate-600">Page {page} / {meta.total_pages}</span>
          <button disabled={page >= meta.total_pages} onClick={() => setPage(p => p + 1)} className="erp-btn-secondary disabled:opacity-50">Next</button>
        </div>
      )}

      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="max-w-xl" data-testid={`${testIdPrefix}-form-dialog`} aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "New"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              {formFields.map(f => (
                <div key={f.key}>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{f.label}{f.required && " *"}</label>
                  {f.type === "select" ? (
                    <div className="mt-1" data-testid={`${testIdPrefix}-${f.key}-input`}>
                      <SearchableSelect
                        options={f.options || []}
                        value={form[f.key] || ""}
                        onValueChange={(v) => setForm({ ...form, [f.key]: v })}
                        placeholder={f.placeholder || "Select..."}
                        allowClear={!f.required}
                      />
                    </div>
                  ) : f.type === "checkbox" ? (
                    <label className="flex items-center gap-2 mt-1 text-sm">
                      <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} />
                      <span>{f.placeholder || f.label}</span>
                    </label>
                  ) : f.type === "multi-select" ? (
                    <div className="mt-1 flex flex-wrap gap-1.5 p-2 border border-slate-200 bg-slate-50 rounded-md max-h-32 overflow-y-auto">
                      {(f.options || []).length === 0 && <span className="text-xs text-slate-400">No options</span>}
                      {(f.options || []).map((opt) => {
                        const checked = (form[f.key] || []).includes(opt.value);
                        return (
                          <label key={opt.value} className={`text-xs px-2 py-1 rounded-sm cursor-pointer border ${checked ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                            <input type="checkbox" className="hidden" checked={checked}
                              onChange={() => setForm((f2) => ({
                                ...f2,
                                [f.key]: checked
                                  ? (f2[f.key] || []).filter((x) => x !== opt.value)
                                  : [...(f2[f.key] || []), opt.value],
                              }))} />
                            {opt.label}
                          </label>
                        );
                      })}
                    </div>
                  ) : f.type === "number" ? (
                    <input type="number" className="erp-input mt-1" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: Number(e.target.value) })} required={f.required} data-testid={`${testIdPrefix}-${f.key}-input`} />
                  ) : f.type === "textarea" ? (
                    <textarea className="erp-input mt-1" rows={3} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={f.required} />
                  ) : (
                    <input type={f.type || "text"} className="erp-input mt-1" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={f.required} placeholder={f.placeholder} data-testid={`${testIdPrefix}-${f.key}-input`} />
                  )}
                </div>
              ))}
              <DialogFooter>
                <button type="button" onClick={() => setOpen(false)} className="erp-btn-secondary">Cancel</button>
                <button type="submit" disabled={submitting} className="erp-btn-primary inline-flex items-center gap-2" data-testid={`${testIdPrefix}-submit-btn`}>
                  {submitting && <Loader2 size={14} className="animate-spin" />} {editing ? "Update" : "Create"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
